import * as stylex from '@stylexjs/stylex';
import type { ReactElement } from 'react';
import { Collection } from 'react-aria-components';

import { type components } from '@/libs/openapi';
import {
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectInput,
  SegmentedButton,
  SegmentedButtonGroup,
  useMediaQuery,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';

interface ReportTypeSelectorProps {
  types: { original: components['schemas']['ReportTypeResponseDto'] }[];
  selectedType: string | undefined;
  onSelectionChange: (type: string) => void;
}

const SEGMENTED_MAX = 6;

export function ReportTypeSelector ({ types, selectedType, onSelectionChange }: ReportTypeSelectorProps): ReactElement | null {
  const isLargeScreen = useMediaQuery(breakpoints.large);

  if (types.length <= 1) {
    return null;
  }

  if (isLargeScreen && types.length <= SEGMENTED_MAX) {
    return (
      <SegmentedButtonGroup
        disallowEmptySelection
        selectedKeys={selectedType ? [ selectedType ] : []}
        onSelectionChange={(keys) => {
          const key = [ ...keys ][0]?.toString();
          if (key !== undefined) {
            onSelectionChange(key);
          }
        }}>
        <Collection items={types}>
          {(item) => (
            <SegmentedButton id={item.original.type}>
              {item.original.name}
            </SegmentedButton>
          )}
        </Collection>
      </SegmentedButtonGroup>
    );
  }

  const handleSelectChange = (key: string | number | null): void => {
    if (key !== null) {
      onSelectionChange(String(key));
    }
  };

  return (
    <Select
      aria-label="Report type"
      selectedKey={selectedType ?? null}
      style={selectStyles.base}
      onSelectionChange={handleSelectChange}>
      <SelectInput />
      <Popover>
        <ListBox items={types}>
          {(item) => (
            <ListBoxItem
              id={item.original.type}
              textValue={item.original.name}>
              {item.original.name}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  );
}

const selectStyles = stylex.create({
  base: {
    width: {
      default: 178,
      [breakpoints.small]: '100%',
    },
  },
});
